import { z } from 'zod';
/**
 * The tool an agent calls to put a question to the person it is talking to.
 *
 * The name is part of the wire contract: a frontend renders the call as a form rather than as a
 * tool card, so both sides match on this constant instead of repeating the literal.
 */
export const AGENTIC_PLATFORM_ASK_USER_TOOL_NAME = 'ask-user';
export const AgenticPlatformAskUserChoiceSchema = z.strictObject({
    description: z.string().max(200).optional(),
    label: z.string().min(1).max(100),
    value: z.string().min(1).max(100),
});
function hasUniqueValues(values) {
    return new Set(values).size === values.length;
}
/** A value identifies the answer, so a repeat would be picked and reported alongside its twin. */
const AgenticPlatformAskUserChoicesSchema = z
    .array(AgenticPlatformAskUserChoiceSchema)
    .min(2)
    .max(10)
    .refine((choices) => hasUniqueValues(choices.map((choice) => choice.value)), {
    error: 'Every choice must have a distinct value.',
});
const AgenticPlatformAskUserFieldBaseShape = {
    id: z.string().min(1).max(50),
    label: z.string().min(1).max(200),
};
/**
 * One answer the agent is asking for.
 *
 * The kinds are separate members rather than one shape with flags so that a choice field cannot
 * be described without choices, and free text cannot be described with them. The members are
 * strict for the same reason: a stray property is the agent describing a control it will not
 * get, and an error it can correct beats a form quietly missing what it meant to ask for.
 */
export const AgenticPlatformAskUserFieldSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        ...AgenticPlatformAskUserFieldBaseShape,
        choices: AgenticPlatformAskUserChoicesSchema,
        kind: z.literal('singleChoice'),
    }),
    z.strictObject({
        ...AgenticPlatformAskUserFieldBaseShape,
        choices: AgenticPlatformAskUserChoicesSchema,
        kind: z.literal('multipleChoice'),
    }),
    z.strictObject({
        ...AgenticPlatformAskUserFieldBaseShape,
        kind: z.literal('shortText'),
        placeholder: z.string().max(100).optional(),
    }),
    z.strictObject({
        ...AgenticPlatformAskUserFieldBaseShape,
        kind: z.literal('longText'),
        placeholder: z.string().max(100).optional(),
    }),
]);
export const AgenticPlatformAskUserQuestionSchema = z.strictObject({
    // An id names one answer, so a repeat would be two questions sharing a single answer.
    fields: z
        .array(AgenticPlatformAskUserFieldSchema)
        .min(1)
        .max(5)
        .refine((fields) => hasUniqueValues(fields.map((field) => field.id)), {
        error: 'Every field must have a distinct id.',
    }),
    question: z.string().min(1).max(500),
});
//# sourceMappingURL=askUser.js.map